import React, { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Phone, PenLine, LogIn, Star, MessageCircleHeart, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Seo from "@/components/Seo";
import { trackAction, trackEvent } from "@/content/site";
import { fetchContent } from "@/lib/api";
import { useGoogleRating } from "@/hooks/useGoogleRating";

/**
 * [Phase 29, 2026-09-22] Review request funnel — /review (EN) + /es/resena (ES).
 *
 * A utility page the firm shares with clients (email signature, SMS, printed
 * QR) that walks them through leaving a Google review. Copy + the Google
 * deep-link come from the backend (`review_page()` in page_content.py) so the
 * place-id stays single-sourced with the live reviews feed. Intentionally
 * noindex — its job is conversion, not rankings.
 *
 * Policy note: the page invites ALL feedback (Google review OR a direct
 * conversation) with equal weight — no review gating.
 */

const STEP_ICONS = [PenLine, LogIn, Star];

// Client-side fallback copy in case /api/content is briefly unavailable —
// mirrors backend review_page() so the CTA still works offline-ish.
const FALLBACK = {
  en: {
    title: "Share Your Experience | Garcia-Windsor, P.C.",
    description: "Leave a Google review for Garcia-Windsor, P.C.",
    h1: "Share Your Experience",
    kicker: "Thank you for trusting us",
    intro: "Your words help other Dallas families find legal help they can trust, in English or Spanish.",
    review_url: "https://search.google.com/local/writereview?placeid=ChIJi99wnMqeToYR_kODtwbgFfA",
    google_cta_label: "Write a review on Google",
    steps_heading: "How it works",
    steps: [
      { h: "Tap the button above", p: "It opens Google's review window for Garcia-Windsor, P.C. directly." },
      { h: "Sign in to Google", p: "Google will ask you to log in with any Gmail or Google account." },
      { h: "Pick your stars and write a few lines", p: "What was your situation? How did our team treat you?" },
    ],
    fallback_heading: "Prefer to talk to us directly?",
    fallback_text: "If anything didn't meet your expectations, we want to hear from you personally.",
    fallback_cta_label: "Contact us",
    contact_href: "/contact-us",
    phone_display: "214-466-6699",
    phone_tel: "+12144666699",
    qr_heading: "Scan to leave us a Google review",
    qr_caption: "Point your phone's camera at the code — it opens our Google review window directly.",
    qr_print_label: "Print QR poster",
    qr_poster_footer: "214-466-6699 · garciawindsor.com/review",
  },
  es: {
    title: "Comparta Su Experiencia | Garcia-Windsor, P.C.",
    description: "Deje una reseña en Google para Garcia-Windsor, P.C.",
    h1: "Comparta Su Experiencia",
    kicker: "Gracias por confiar en nosotros",
    intro: "Sus palabras ayudan a otras familias de Dallas a encontrar ayuda legal de confianza, en español o inglés.",
    review_url: "https://search.google.com/local/writereview?placeid=ChIJi99wnMqeToYR_kODtwbgFfA",
    google_cta_label: "Escribir una reseña en Google",
    steps_heading: "Cómo funciona",
    steps: [
      { h: "Toque el botón de arriba", p: "Abre directamente la ventana de reseñas de Google para Garcia-Windsor, P.C." },
      { h: "Inicie sesión en Google", p: "Google le pedirá entrar con cualquier cuenta de Gmail o Google." },
      { h: "Elija sus estrellas y escriba unas líneas", p: "¿Cuál era su situación? ¿Cómo la trató nuestro equipo?" },
    ],
    fallback_heading: "¿Prefiere hablar con nosotros directamente?",
    fallback_text: "Si algo no cumplió sus expectativas, queremos escucharle personalmente.",
    fallback_cta_label: "Contáctenos",
    contact_href: "/es/contactenos",
    phone_display: "214-466-6699",
    phone_tel: "+12144666699",
    qr_heading: "Escanee para dejarnos una reseña en Google",
    qr_caption: "Apunte la cámara de su teléfono al código — abre directamente la ventana de reseñas de Google.",
    qr_print_label: "Imprimir cartel con QR",
    qr_poster_footer: "214-466-6699 · garciawindsor.com/es/resena",
  },
};

export default function ReviewPage({ lang }) {
  const path = lang === "es" ? "/es/resena" : "/review";
  const pair = lang === "es" ? "/review" : "/es/resena";

  const { data } = useQuery({
    queryKey: ["content", path],
    queryFn: () => fetchContent(path),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  const page = data?.page || FALLBACK[lang];
  const live = useGoogleRating(lang);

  const handleGoogleClick = useCallback(() => {
    trackAction("google_reviews_click", {
      link_domain: "search.google.com",
      location: "review_page",
      review_count: live?.available ? live.count : undefined,
      rating_value: live?.available ? live.rating : undefined,
    });
  }, [live]);

  const handleContactClick = useCallback(() => {
    trackEvent("cta_click", { location: "review_page", label: "contact_fallback" });
  }, []);

  const handlePhoneClick = useCallback(() => {
    trackEvent("phone_click", { location: "review_page", page: path });
  }, [path]);

  const handlePrintClick = useCallback(() => {
    trackEvent("review_qr_print", { location: "review_page", page: path });
    window.print();
  }, [path]);

  const fullStars = live?.available ? Math.round(live.rating) : 5;
  const ratingLine = live?.available
    ? (lang === "es"
        ? `${live.rating.toFixed(1)} de 5 — ${live.count} reseñas de clientes en Google`
        : `${live.rating.toFixed(1)} out of 5 — ${live.count} client reviews on Google`)
    : (lang === "es" ? "Calificado por clientes en Google" : "Rated by clients on Google");

  return (
    <div className="gw-review-page" data-testid="review-page">
      <Seo page={page} path={path} lang={lang} pair={pair} noindex />

      <section className="gw-review-hero">
        <div className="gw-container gw-review-hero-inner">
          <p className="gw-eyebrow gw-review-kicker" data-testid="review-kicker">{page.kicker}</p>
          <h1 data-testid="review-h1">{page.h1}</h1>
          <p className="gw-review-intro" data-testid="review-intro">{page.intro}</p>

          <div className="gw-review-rating" data-testid="review-rating">
            <span className="gw-review-stars" role="img" aria-label={ratingLine}>
              {"★".repeat(fullStars)}
              {"☆".repeat(Math.max(0, 5 - fullStars))}
            </span>
            <span className="gw-review-rating-text">{ratingLine}</span>
          </div>

          <a
            href={page.review_url}
            target="_blank"
            rel="noopener noreferrer"
            className="gw-btn gw-btn-accent gw-review-cta"
            onClick={handleGoogleClick}
            data-testid="review-google-cta"
          >
            <MessageCircleHeart size={20} strokeWidth={2} aria-hidden="true" />
            {page.google_cta_label}
          </a>
        </div>
      </section>

      <section className="gw-review-steps-wrap">
        <div className="gw-container">
          <h2 data-testid="review-steps-heading">{page.steps_heading}</h2>
          <ol className="gw-review-steps" data-testid="review-steps">
            {(page.steps || []).map((step, i) => {
              const Icon = STEP_ICONS[i] || Star;
              return (
                <li className="gw-review-step" key={step.h} data-testid={`review-step-${i + 1}`}>
                  <span className="gw-review-step-icon" aria-hidden="true">
                    <Icon size={22} strokeWidth={2} />
                  </span>
                  <span className="gw-review-step-num" aria-hidden="true">{i + 1}</span>
                  <h3>{step.h}</h3>
                  <p>{step.p}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="gw-review-qr-wrap">
        <div className="gw-container">
          <div className="gw-review-qr-card" id="review-qr" data-testid="review-qr-card">
            {/* The poster block is the ONLY thing visible when printing —
                see the @media print rules below. */}
            <div className="gw-review-print-poster" data-testid="review-qr-poster">
              <p className="gw-review-poster-firm">Garcia-Windsor, P.C.</p>
              <h2 className="gw-review-poster-heading">{page.qr_heading}</h2>
              <div className="gw-review-qr-box" data-testid="review-qr-code">
                <QRCodeSVG
                  value={page.review_url}
                  size={200}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#3B0A14"
                  title={page.qr_heading}
                />
              </div>
              <p className="gw-review-poster-caption">{page.qr_caption}</p>
              <p className="gw-review-poster-footer">{page.qr_poster_footer}</p>
            </div>
            <button
              type="button"
              className="gw-btn gw-review-print-btn"
              onClick={handlePrintClick}
              data-testid="review-qr-print-btn"
            >
              <Printer size={17} strokeWidth={2.2} aria-hidden="true" />
              {page.qr_print_label}
            </button>
          </div>
        </div>
      </section>

      <section className="gw-review-fallback-wrap">
        <div className="gw-container">
          <div className="gw-review-fallback" data-testid="review-fallback">
            <h2>{page.fallback_heading}</h2>
            <p>{page.fallback_text}</p>
            <div className="gw-review-fallback-actions">
              <Link
                to={page.contact_href}
                className="gw-btn gw-review-contact-btn"
                onClick={handleContactClick}
                data-testid="review-contact-link"
              >
                {page.fallback_cta_label}
              </Link>
              <a
                href={`tel:${page.phone_tel}`}
                className="gw-review-phone"
                onClick={handlePhoneClick}
                data-testid="review-phone-link"
              >
                <Phone size={16} strokeWidth={2.2} aria-hidden="true" />
                {page.phone_display}
              </a>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .gw-review-page { background: var(--gw-bg, #FAF8F5); }
        .gw-review-hero {
          background: var(--gw-primary-dark, #3B0A14);
          color: #fff;
          padding: var(--gw-space-10, 4rem) 0 var(--gw-space-9, 3.5rem);
          text-align: center;
        }
        .gw-review-hero-inner { max-width: 720px; margin: 0 auto; }
        .gw-review-kicker { color: var(--gw-accent, #D4A017); }
        .gw-review-hero h1 { color: #fff; margin: 0.4rem 0 1rem; }
        .gw-review-intro {
          color: rgba(255, 255, 255, 0.88);
          font-size: 1.05rem;
          margin: 0 auto 1.5rem;
          max-width: 56ch;
        }
        .gw-review-rating {
          display: flex; flex-direction: column; align-items: center;
          gap: var(--gw-space-1, 0.25rem); margin-bottom: 1.75rem;
        }
        .gw-review-stars {
          color: var(--gw-accent, #D4A017);
          font-size: 1.5rem; letter-spacing: 4px; line-height: 1;
        }
        .gw-review-rating-text { color: rgba(255,255,255,0.85); font-size: 0.95rem; }
        .gw-review-cta {
          display: inline-flex; align-items: center; gap: 0.6rem;
          font-size: 1.05rem; padding: 0.9rem 1.8rem; min-height: 52px;
        }
        .gw-review-steps-wrap { padding: var(--gw-space-9, 3.5rem) 0; }
        .gw-review-steps-wrap h2 { text-align: center; margin-bottom: 2rem; }
        .gw-review-steps {
          list-style: none; margin: 0 auto; padding: 0;
          display: grid; gap: var(--gw-space-5, 1.5rem);
          grid-template-columns: repeat(3, 1fr); max-width: 960px;
        }
        .gw-review-step {
          position: relative;
          background: #fff;
          border: 1px solid var(--gw-border, #E8E0D6);
          border-radius: var(--gw-radius, 10px);
          padding: 1.5rem 1.25rem 1.4rem;
          box-shadow: 0 2px 10px rgba(59, 10, 20, 0.05);
        }
        .gw-review-step-icon {
          display: inline-flex; align-items: center; justify-content: center;
          width: 44px; height: 44px; border-radius: 50%;
          background: rgba(122, 27, 42, 0.08);
          color: var(--gw-primary, #7A1B2A);
          margin-bottom: 0.75rem;
        }
        .gw-review-step-num {
          position: absolute; top: 1rem; right: 1.1rem;
          font-family: var(--gw-font-serif, Georgia, serif);
          font-size: 1.6rem; color: var(--gw-border, #E8E0D6); font-weight: 700;
        }
        .gw-review-step h3 { margin: 0 0 0.4rem; font-size: 1.05rem; }
        .gw-review-step p { margin: 0; font-size: 0.92rem; color: var(--gw-muted, #5B5148); }
        .gw-review-fallback-wrap { padding: 0 0 var(--gw-space-10, 4rem); }
        .gw-review-qr-wrap { padding: 0 0 var(--gw-space-9, 3.5rem); }
        .gw-review-qr-card {
          max-width: 720px; margin: 0 auto; text-align: center;
          background: #fff;
          border: 1px solid var(--gw-border, #E8E0D6);
          border-radius: var(--gw-radius, 10px);
          padding: 2rem 1.75rem;
          display: flex; flex-direction: column; align-items: center;
          gap: var(--gw-space-4, 1rem);
        }
        .gw-review-print-poster {
          display: flex; flex-direction: column; align-items: center;
          gap: var(--gw-space-3, 0.75rem);
        }
        .gw-review-poster-firm {
          font-family: var(--gw-font-serif, Georgia, serif);
          color: var(--gw-primary, #7A1B2A);
          letter-spacing: 0.12em; text-transform: uppercase;
          font-size: 0.85rem; font-weight: 700; margin: 0;
        }
        .gw-review-poster-heading { margin: 0; font-size: 1.35rem; }
        .gw-review-qr-box {
          background: #fff;
          border: 1px solid var(--gw-border, #E8E0D6);
          border-radius: var(--gw-radius, 10px);
          padding: 1rem; line-height: 0;
        }
        .gw-review-poster-caption {
          color: var(--gw-muted, #5B5148); font-size: 0.95rem;
          max-width: 44ch; margin: 0;
        }
        .gw-review-poster-footer {
          color: var(--gw-primary, #7A1B2A); font-weight: 700;
          font-size: 0.95rem; margin: 0;
        }
        .gw-review-print-btn {
          display: inline-flex; align-items: center; gap: 0.5rem;
          background: var(--gw-primary-dark, #3B0A14); color: #fff;
          min-height: 44px;
        }
        .gw-review-print-btn:hover,
        .gw-review-print-btn:focus-visible { background: var(--gw-primary, #7A1B2A); color: #fff; }
        @media print {
          /* Poster-only print: hide the whole app, show a clean centered
             letter-size poster. visibility (not display) so the poster's
             layout box is preserved. */
          body * { visibility: hidden !important; }
          .gw-review-print-poster, .gw-review-print-poster * { visibility: visible !important; }
          .gw-review-print-poster {
            position: fixed; inset: 0;
            display: flex; flex-direction: column;
            align-items: center; justify-content: center;
            gap: 1.25rem; background: #fff; padding: 2rem;
          }
          .gw-review-poster-firm { font-size: 1.3rem; }
          .gw-review-poster-heading { font-size: 2rem; max-width: 20ch; }
          .gw-review-qr-box svg { width: 340px !important; height: 340px !important; }
          .gw-review-poster-caption { font-size: 1.15rem; max-width: 40ch; }
          .gw-review-poster-footer { font-size: 1.15rem; }
        }
        .gw-review-fallback {
          max-width: 720px; margin: 0 auto; text-align: center;
          background: #fff;
          border: 1px solid var(--gw-border, #E8E0D6);
          border-left: 4px solid var(--gw-primary, #7A1B2A);
          border-radius: var(--gw-radius, 10px);
          padding: 2rem 1.75rem;
        }
        .gw-review-fallback h2 { margin: 0 0 0.6rem; font-size: 1.35rem; }
        .gw-review-fallback p { color: var(--gw-muted, #5B5148); margin: 0 auto 1.25rem; max-width: 52ch; }
        .gw-review-fallback-actions {
          display: flex; flex-wrap: wrap; gap: var(--gw-space-4, 1rem);
          align-items: center; justify-content: center;
        }
        .gw-review-contact-btn {
          background: var(--gw-primary, #7A1B2A); color: #fff; min-height: 44px;
        }
        .gw-review-contact-btn:hover,
        .gw-review-contact-btn:focus-visible { background: var(--gw-primary-dark, #3B0A14); color: #fff; }
        .gw-review-phone {
          display: inline-flex; align-items: center; gap: 0.45rem;
          color: var(--gw-primary, #7A1B2A); font-weight: 700;
          text-decoration: none; min-height: 44px;
        }
        .gw-review-phone:hover, .gw-review-phone:focus-visible { text-decoration: underline; }
        @media (max-width: 768px) {
          .gw-review-steps { grid-template-columns: 1fr; max-width: 480px; }
          .gw-review-hero { padding: var(--gw-space-8, 3rem) 0; }
        }
      `}</style>
    </div>
  );
}
